import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes, { object } from 'prop-types'

import CampaignRuntime from './CampaignRuntime'
import { SecondaryButton } from 'components/Shared/Button'
import EachStat from './EventStatisticsItem'
import { numberWithThousandsSeparators } from 'utils/helpers'
import { ImpressionsImg, PlaylistIcon, TouchImg, WalletIcon, ReachIcon, CTRIcon } from 'images'
import InfoBox from 'components/Shared/InfoBox'
import moment from 'moment'
import { useAdCreatives } from 'hooks/useAdCreatives'
import PreviewAdsModal from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/PreviewAdsModal'
import {
  usePublishedCampaignSetups,
  findTasteCluster,
  toSetupPlatformField,
  tcPlatformField,
} from 'hooks/usePublishedCampaignSetups'
import {
  deriveCampaignTypeFromSetup,
  toApiCampaignType,
} from 'components/Recommendations/EventCampaigns/Steps/Setup/Goals/campaignType'
import PlatformToggle, { useHasMultiplePlatforms } from 'components/Shared/PlatformToggle'
import eventEvaluationApi from 'domain/eventEvaluation/api'
import AccordionCard from 'components/Shared/AccordionCard'

const EventStatsOverview = ({
  currency,
  eventId,
  runningCampaigns,
  setAdCreatives,
  onInsightsChange,
}) => {
  const { t } = useTranslation()
  const {
    eventCampaign,
    tasteClusters,
    loading: campaignSetupsLoading,
  } = usePublishedCampaignSetups(eventId)
  const [isModalOpen, setModalOpen] = useState(false)
  const [platform, setPlatform] = useState('all')
  const hasMultiplePlatforms = useHasMultiplePlatforms()
  const [insights, setInsights] = useState(null)
  const [insightsLoading, setInsightsLoading] = useState(true)
  const [isAccordionOpen, setIsAccordionOpen] = useState(true)

  const handlePlatformChange = (newPlatform) => {
    setPlatform(newPlatform)
    setIsAccordionOpen(true)
  }

  useEffect(() => {
    if (!eventId) return

    let cancelled = false
    setInsightsLoading(true)

    eventEvaluationApi.fetchInsightsAsync(eventId, platform).then(({ data }) => {
      if (cancelled) return
      setInsights(data ?? null)
      setInsightsLoading(false)
      onInsightsChange?.(data ?? null)
    })

    return () => {
      cancelled = true
    }
  }, [eventId, platform, onInsightsChange])

  const googleCampaignType = useMemo(
    () =>
      toApiCampaignType(
        deriveCampaignTypeFromSetup({
          platform_overrides: eventCampaign?.platformOverrides ?? null,
        }),
      ),
    [eventCampaign],
  )

  const campaignsAdDetails = useMemo(() => {
    if (runningCampaigns.length)
      return runningCampaigns.map((campaign) => {
        const tasteCluster = findTasteCluster(tasteClusters, campaign.tc, campaign.tc_run_id)

        return {
          creatives: { ...tasteCluster?.creative, tc: campaign.tc },
          integration_details: eventCampaign?.integrationDetails,
          platforms: tcPlatformField(tasteCluster, eventCampaign),
          campaignType: googleCampaignType,
        }
      })
    return []
  }, [runningCampaigns, tasteClusters, eventCampaign, googleCampaignType])

  const disabledAdVariations = useMemo(() => {
    if (runningCampaigns.length)
      return runningCampaigns.reduce((acc, campaign) => {
        const creative = findTasteCluster(tasteClusters, campaign.tc, campaign.tc_run_id)?.creative
        if (creative) {
          const disabledVariations = creative.excluded_variations
          if (disabledVariations.length) {
            acc[campaign.tc] = disabledVariations
          }
        }
        return acc
      }, {})
    return {}
  }, [runningCampaigns, tasteClusters])

  const { totalAds, totalAdsByPlatform, loading, fbPageInfo } = useAdCreatives(
    campaignsAdDetails,
    setAdCreatives,
    { ready: !campaignSetupsLoading },
  )

  // Pseudo-`setup` for `PreviewAdsModal` (domain/adPreview/fromSetup.js) —
  // aggregates every running campaign's creative into one `creatives` array
  // (unlike RunningCampaigns.jsx's per-TC-scoped setup, this dashboard button
  // previews all of the event's taste clusters at once, same as the wizard's
  // Summary step). `platform`/`platform_overrides` are event-wide, derived
  // the same way toCampaignSetupCompat derives them per-TC.
  const previewSetup = useMemo(() => {
    const creatives = runningCampaigns
      .map((campaign) => {
        const creative = findTasteCluster(tasteClusters, campaign.tc, campaign.tc_run_id)?.creative
        return creative && { ...creative, tc: campaign.tc, tc_run_id: campaign.tc_run_id }
      })
      .filter(Boolean)

    return {
      platform: toSetupPlatformField(eventCampaign?.platforms),
      platform_overrides: eventCampaign?.platformOverrides ?? null,
      creatives,
    }
  }, [runningCampaigns, tasteClusters, eventCampaign])

  const impressions = insights?.impressions
  const clicks = insights?.clicks
  const reach = insights?.reach
  const ctr = insights?.ctr
  const adSpend = insights?.adSpend
  const numOfAds = platform === 'all' ? totalAds : (totalAdsByPlatform[platform] ?? 0)
  const startDate = insights?.start ? moment.utc(insights?.start).format('DD.MM.YYYY') : null
  const endDate = insights?.end ? moment.utc(insights?.end).format('DD.MM.YYYY') : null

  return (
    <div className="event-series-stats">
      <AccordionCard
        isOpen={isAccordionOpen}
        onToggle={setIsAccordionOpen}
        title={
          <div className="header-with-platform-toggle">
            <div className="event-statistics">
              <InfoBox withAnchor={false}>
                <h2 className="card-title-default d-inline-block mb-0">
                  {t('Dashboard.eventSeriesStats.title')}
                </h2>
              </InfoBox>
              <div className="mt-2">
                <CampaignRuntime startDate={startDate} endDate={endDate} />
              </div>
            </div>

            <div className="d-flex align-items-center mr-3" style={{ gap: '16px' }}>
              {hasMultiplePlatforms && (
                <>
                  <PlatformToggle
                    platform={platform}
                    setPlatform={handlePlatformChange}
                    disabled={loading}
                  />
                  <div style={{ width: 1, alignSelf: 'stretch', background: '#C1C5D3' }} />
                </>
              )}
              <div className="summary_item_button">
                <SecondaryButton
                  disabled={loading}
                  text={t('Recommendations.summary.previewAds')}
                  color="navy"
                  size="small"
                  onClick={() => setModalOpen(true)}
                />
              </div>
            </div>
          </div>
        }
      >
        <div className="row">
          <div className="col">
            <EachStat
              title={t('Dashboard.eventSeriesStats.adSpend')}
              renderIcon={() => <WalletIcon />}
              value={numberWithThousandsSeparators(adSpend, { currency, maxFractionDigits: 0 })}
              loading={insightsLoading}
            />
          </div>
          <div className="col">
            <EachStat
              title={t('Dashboard.eventSeriesStats.impressions')}
              imgSrc={ImpressionsImg}
              renderIcon={() => <img src={ImpressionsImg} alt="" />}
              value={numberWithThousandsSeparators(impressions)}
              loading={insightsLoading}
            />
          </div>
          <div className="col m-0">
            <EachStat
              title={t('Dashboard.eventSeriesStats.clicks')}
              renderIcon={() => <img src={TouchImg} alt="" />}
              value={numberWithThousandsSeparators(clicks)}
              loading={insightsLoading}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <EachStat
              title={t('Dashboard.eventSeriesStats.reach')}
              renderIcon={() => <ReachIcon />}
              value={numberWithThousandsSeparators(reach)}
              loading={insightsLoading}
            />
          </div>
          <div className="col m-0">
            <EachStat
              title={t('Dashboard.eventSeriesStats.ctr')}
              imgSrc={ImpressionsImg}
              renderIcon={() => <CTRIcon />}
              value={numberWithThousandsSeparators(ctr, { maxFractionDigits: 2, suffix: '%' })}
              loading={insightsLoading}
            />
          </div>
          <div className="col m-0">
            <EachStat
              title={t('Dashboard.eventSeriesStats.totalAds')}
              imgSrc={ImpressionsImg}
              renderIcon={() => <PlaylistIcon />}
              value={numOfAds}
              loading={loading}
            />
          </div>
        </div>

        <PreviewAdsModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          creatives={previewSetup.creatives}
          setup={previewSetup}
          pageInfo={fbPageInfo?.content}
          disabledVariations={disabledAdVariations}
        />
      </AccordionCard>
    </div>
  )
}

EventStatsOverview.propTypes = {
  currency: PropTypes.string,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  runningCampaigns: PropTypes.arrayOf(object),
  setAdCreatives: PropTypes.func,
  onInsightsChange: PropTypes.func,
}

export default EventStatsOverview
